import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Gift, Coins, Plus, Trash, CheckCircle, XCircle, Star } from "@phosphor-icons/react";

const CATS = [
  { v: "perk", label: "Perk", icon: "☕" },
  { v: "voucher", label: "Voucher", icon: "🎟️" },
  { v: "swag", label: "Swag", icon: "🎁" },
  { v: "timeoff", label: "Time off", icon: "🏖️" },
  { v: "experience", label: "Experience", icon: "✨" },
];

const REDEMPTION_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  fulfilled: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

export default function Rewards() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const [rewards, setRewards] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, earned: 0, redeemed: 0 });
  const [redemptions, setRedemptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", points_required: 100, stock: 100, category: "perk" });

  const load = () => {
    api.get("/rewards", { params }).then(({ data }) => setRewards(data)).catch(() => {});
    if (user?.employee_id) api.get("/rewards/balance").then(({ data }) => setBalance(data));
    api.get("/rewards/redemptions", { params }).then(({ data }) => setRedemptions(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const payload = { ...form, points_required: parseInt(form.points_required), stock: parseInt(form.stock) };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/rewards", payload);
      toast.success("Reward added");
      setOpen(false);
      setForm({ title: "", description: "", points_required: 100, stock: 100, category: "perk" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const redeem = async (rid) => {
    try { await api.post("/rewards/redeem", { reward_id: rid }); toast.success("Redemption requested"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const decide = async (rid, action) => { try { await api.patch(`/rewards/redemptions/${rid}?action=${action}`); toast.success(action); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Recognition · Rewards</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Rewards catalogue</h1>
          <p className="text-sm text-zinc-500 mt-2">Turn kudos points into real perks — from coffee vouchers to a day off.</p>
        </div>
        {user?.employee_id && (
          <div className="border border-adb-green/40 bg-gradient-to-br from-adb-green/10 to-white rounded-sm px-5 py-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Your balance</div>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex items-center gap-1.5">
                <Star size={22} weight="fill" className="text-adb-green" />
                <div className="font-display text-3xl font-bold text-adb-navy font-mono-plex" data-testid="rewards-balance">{balance.balance}</div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono-plex">
                Earned {balance.earned} · Redeemed {balance.redeemed}
              </div>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="catalogue">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="catalogue" className="rounded-sm" data-testid="rew-tab-cat"><Gift size={14} className="mr-1" /> Catalogue</TabsTrigger>
          <TabsTrigger value="redemptions" className="rounded-sm" data-testid="rew-tab-red"><Coins size={14} className="mr-1" /> Redemptions</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogue" className="mt-4 space-y-3">
          {isHR && (
            <div className="flex justify-end">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="rew-add-btn"><Plus size={16} className="mr-1.5" /> Add reward</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl">New reward</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="rew-title-input" /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
                    <div className="space-y-1"><Label className="text-xs">Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.icon} {c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Points required</Label><Input type="number" value={form.points_required} onChange={(e) => setForm({ ...form, points_required: e.target.value })} className="rounded-sm font-mono-plex" data-testid="rew-points-input" /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-xs">Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button><Button onClick={save} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="rew-save-btn">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewards.length === 0 && <div className="col-span-full border border-zinc-200 bg-white rounded-sm p-12 text-center text-sm text-zinc-500">No rewards in catalogue yet.</div>}
            {rewards.map((r) => {
              const cat = CATS.find((c) => c.v === r.category) || CATS[0];
              const affordable = balance.balance >= r.points_required;
              return (
                <div key={r.id} className="border border-zinc-200 bg-white rounded-sm p-5 hover:border-adb-navy/40 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between">
                    <div className="text-4xl">{cat.icon}</div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-adb-navy font-mono-plex">
                        <Star size={14} weight="fill" className="text-adb-green" />
                        <span className="font-display text-lg font-bold">{r.points_required}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500">{r.stock} in stock</div>
                    </div>
                  </div>
                  <div className="font-display text-lg font-bold mt-3">{r.title}</div>
                  {r.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{r.description}</div>}
                  <div className="mt-4 flex items-center justify-between">
                    <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 capitalize text-[10px]">{cat.label}</Badge>
                    {user?.employee_id && (
                      <Button size="sm" disabled={!affordable || r.stock <= 0} onClick={() => redeem(r.id)} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800 h-7" data-testid={`rew-redeem-${r.id}`}>
                        {r.stock <= 0 ? "Out" : affordable ? "Redeem" : `Need ${r.points_required - balance.balance}`}
                      </Button>
                    )}
                    {isHR && !user?.employee_id && (
                      <button onClick={async () => { if (window.confirm("Delete reward?")) { await api.delete(`/rewards/${r.id}`); load(); } }} className="text-red-600 hover:opacity-70"><Trash size={14} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="redemptions" className="mt-4">
          <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
            <table className="hr-table">
              <thead><tr><th>Reward</th><th>Points spent</th><th>Status</th><th>Requested</th>{isHR && <th></th>}</tr></thead>
              <tbody>
                {redemptions.length === 0 && <tr><td colSpan={isHR ? 5 : 4} className="text-center py-6 text-zinc-500 text-sm">No redemptions.</td></tr>}
                {redemptions.map((r) => (
                  <tr key={r.id}>
                    <td>{r.reward_title}</td>
                    <td className="font-mono-plex text-xs font-semibold">{r.points_spent}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${REDEMPTION_STYLE[r.status]}`}>{r.status}</Badge></td>
                    <td className="font-mono-plex text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    {isHR && (
                      <td>
                        {r.status === "pending" && (
                          <div className="flex gap-1">
                            <button onClick={() => decide(r.id, "fulfilled")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`rew-fulfil-${r.id}`}><CheckCircle size={12} className="inline mr-1" /> Fulfil</button>
                            <button onClick={() => decide(r.id, "cancelled")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm"><XCircle size={12} className="inline mr-1" /> Cancel</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
